import React, { Component } from 'react';
import styled from 'styled-components';
import {
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
  List
} from 'react-virtualized';
import { connect } from 'react-redux';

import { isiOS, appsPerPage } from 'config';
import {
  iOSNavbarHeightNumber,
  androidNavbarTopRowHeight,
  androidNavbarBottomRowHeight
} from 'themes';
import AndroidSpinner from 'inline-icons/android-loading.svg';
import IOSSpinner from 'inline-icons/iOS-loading.svg';

const DEFAULT_HEIGHT = 160;

const StyledVirtualizedList = styled(List)`
  background-color: white;
`;

const LoadingRow = styled.div.attrs({
  dangerouslySetInnerHTML: {
    __html: isiOS ? IOSSpinner : AndroidSpinner
  }
})`
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  min-height: 100px;
  svg {
    ${({ theme }) => theme.LoadingIcon}
  }
`;

class VirtualizedList extends Component {
  _cache = new CellMeasurerCache({
    defaultHeight: DEFAULT_HEIGHT,
    fixedWidth: true,
    keyMapper: this.cacheKeyMapper
  });

  _storeKey = 'list-store';

  state = {
    scrollTop: 0,
    loading: false,
    loadingScroll: true
  };

  componentDidMount() {
    if (
      this.props.location.kind === 'pop' &&
      this.props.location.prev.type === 'APP'
    ) {
      this.loadScrollPosition();
      return;
    }
    this.clearScrollPosition();
  }

  componentWillUnmount() {
    this.saveScrollPosition();
  }

  clearScrollPosition() {
    sessionStorage.removeItem(this._storeKey);
    this.setState({
      loadingScroll: false
    });
  }

  loadScrollPosition() {
    const storeJSON = sessionStorage.getItem(this._storeKey);
    const {
      cellHeightCache = {},
      rowHeightCache = {},
      scrollTop = 0
    } = storeJSON ? JSON.parse(storeJSON) : {};
    this._cache._rowHeightCache = rowHeightCache;
    this._cache._cellHeightCache = cellHeightCache;
    this.setState({
      scrollTop,
      loadingScroll: false
    });
  }

  saveScrollPosition() {
    sessionStorage.setItem(
      this._storeKey,
      JSON.stringify({
        cellHeightCache: this._cache._cellHeightCache,
        rowHeightCache: this._cache._rowHeightCache,
        scrollTop: this.state.scrollTop
      })
    );
  }

  renderRow = ({
    key, // Unique key within array of rows
    index, // Index of row within collection
    parent,
    style // Style object to be applied to row (to position it)
  }) => {
    const { currentCount, children } = this.props;

    if (!children) {
      throw new Error('children render prop is required!');
    }

    let itemContent;

    // This ensures an additional row to show loading UX
    // to let the user know the next page is loading
    if (index === currentCount && this.state.loading) {
      itemContent = <LoadingRow style={style} />;
    } else {
      itemContent = children({ index, style });
    }

    return (
      <CellMeasurer
        cache={this._cache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        {itemContent}
      </CellMeasurer>
    );
  };

  cacheKeyMapper = (index) => {
    if (index === this.props.currentCount && this.state.loading) {
      return index;
    }

    // This is used to optimize row height lookup by using
    // the first computed height for every row
    return 0;
  };

  onScroll = ({ scrollTop }) => {
    this.setState({ scrollTop });
  };

  onLoadMoreRows = async (loadMoreArgs) => {
    const loadingRow = this.props.currentCount;

    this.setState({ loading: true });
    await this.props.fetchMore(loadMoreArgs);
    this.setState({ loading: false }, () =>
      // Guarantee that it will resize the row to the
      // normal content once loading is complete
      this._cache.clear(loadingRow)
    );
  };

  render() {
    const {
      maxCount = 10000,
      pageLoadThreshold = 6,
      pageSize = appsPerPage,
      currentCount = 0,
      fullNavbar,
      isItemLoaded
    } = this.props;

    const { loading, scrollTop } = this.state;

    return (
      <InfiniteLoader
        isRowLoaded={isItemLoaded}
        loadMoreRows={this.onLoadMoreRows}
        rowCount={maxCount}
        threshold={pageLoadThreshold}
        minimumBatchSize={pageSize}
      >
        {({ onRowsRendered, registerChild }) => (
          <StyledVirtualizedList
            width={window.innerWidth}
            height={
              window.innerHeight -
              (isiOS
                ? iOSNavbarHeightNumber
                : androidNavbarTopRowHeight +
                  (fullNavbar ? androidNavbarBottomRowHeight : 0))
            }
            estimatedRowSize={DEFAULT_HEIGHT}
            onScroll={this.onScroll}
            rowCount={currentCount + (loading ? 1 : 0)}
            rowHeight={this._cache.rowHeight}
            rowRenderer={this.renderRow}
            scrollTop={scrollTop}
            onRowsRendered={onRowsRendered}
            ref={registerChild}
          />
        )}
      </InfiniteLoader>
    );
  }
}

const mapStateToProps = ({ location }) => ({
  location,
  fullNavbar: location.type === 'GALLERY'
});

export default connect(mapStateToProps)(VirtualizedList);
